import type { ILog } from '../../interface/log';
import consoleLoggerInstantiator from './log.console';
import { ILogger, LOG_LEVEL } from './log.if';

const ENV_IS_DEVELOPMENT = process.env['NODE_ENV'] !== 'production';

const LOG_LEVEL_DEV = [
    LOG_LEVEL.MILESTONE,
    LOG_LEVEL.TRACE,
    LOG_LEVEL.VERBOSE,
    LOG_LEVEL.INFO,
    LOG_LEVEL.WARN,
    LOG_LEVEL.ERROR,
    LOG_LEVEL.FATAL,
    LOG_LEVEL.ASSERT,
];
const LOG_LEVEL_PROD = [LOG_LEVEL.WARN, LOG_LEVEL.ERROR, LOG_LEVEL.FATAL, LOG_LEVEL.ASSERT];

const _logLevels = (): Array<LOG_LEVEL> => (ENV_IS_DEVELOPMENT ? LOG_LEVEL_DEV : LOG_LEVEL_PROD);

const _buildLoggers = (logLevels: Array<LOG_LEVEL>): Array<ILogger> => [
    consoleLoggerInstantiator(logLevels),
];

function logger(): ILog {
    const logLevels = _logLevels();
    const loggers: Array<ILogger> = _buildLoggers(logLevels);

    const isLogLevelEnabled = (logLevel: LOG_LEVEL) => logLevels.includes(logLevel);

    const logAll = (logLevel: LOG_LEVEL, message: string) => {
        for (let i = 0; i < loggers.length; i++) {
            loggers[i].log(logLevel, message);
        }
    };

    const assertAll = (condition: boolean | string | number, message: string) => {
        for (let i = 0; i < loggers.length; i++) {
            loggers[i].assert(condition, message);
        }
    };

    const logNoop = (_: string) => {
        /* noop */
    };

    const logAssertNoop = (_: boolean | string | number, __: string | undefined) => {
        /* noop */
    };

    const module: ILog = {
        milestone: isLogLevelEnabled(LOG_LEVEL.MILESTONE)
            ? (msg: string) => logAll(LOG_LEVEL.MILESTONE, msg)
            : logNoop,
        verbose: isLogLevelEnabled(LOG_LEVEL.VERBOSE)
            ? (msg: string) => logAll(LOG_LEVEL.VERBOSE, msg)
            : logNoop,
        trace: isLogLevelEnabled(LOG_LEVEL.TRACE)
            ? (msg: string) => logAll(LOG_LEVEL.TRACE, msg)
            : logNoop,
        info: isLogLevelEnabled(LOG_LEVEL.INFO)
            ? (msg: string) => logAll(LOG_LEVEL.INFO, msg)
            : logNoop,
        warn: isLogLevelEnabled(LOG_LEVEL.WARN)
            ? (msg: string) => logAll(LOG_LEVEL.WARN, msg)
            : logNoop,
        warning: isLogLevelEnabled(LOG_LEVEL.WARN)
            ? (msg: string) => logAll(LOG_LEVEL.WARN, msg)
            : logNoop,
        error: isLogLevelEnabled(LOG_LEVEL.ERROR)
            ? (msg: string) => logAll(LOG_LEVEL.ERROR, msg)
            : logNoop,
        fatal: isLogLevelEnabled(LOG_LEVEL.FATAL)
            ? (msg: string) => logAll(LOG_LEVEL.FATAL, msg)
            : logNoop,
        assert: isLogLevelEnabled(LOG_LEVEL.ASSERT)
            ? (condition: boolean | string | number, message: string | undefined) =>
                  assertAll(condition, message ?? 'Assertion check failed')
            : logAssertNoop,
    };

    return module;
}

export default logger;
